import isNil from "lodash/isNil";

import { GroupRegistrationText } from "@/constants/Schedule";
import { ExploreTestID } from "@/constants/TestID";
import GroupSectionDetailRow from "@/ui/components/groupClasses/series/session/GroupSectionDetailRow";
import { Button, FAIconName, TextTag } from "@/ui/core";
import { BaseCard, HStack, VStack } from "@/ui/layout";
import { joinTestIDs, pluralize } from "@/utils/string.util";

interface RegisteredSectionCardProps {
  // schedule event view: the tapped occurrence's date, shown first as a plain date
  eventDateString?: string;
  // group details view: the section's next event, shown as "Next on ..." with a forward icon
  nextDateString?: string;
  // when provided (schedule event view), shows a "Decline event" button beside "Edit registration"
  onDeclineEventPress?: () => void;
  // omitted for pre-treatment users — the group can't be edited until treatment starts
  onEditPress?: () => void;
  recurrenceString: string;
  sessionCount: number;
  timeRange: string;
}

const baseTestID = ExploreTestID.GroupClassRegisteredSectionCard;

const RegisteredSectionCard = ({
  eventDateString,
  nextDateString,
  onDeclineEventPress,
  onEditPress,
  recurrenceString,
  sessionCount,
  timeRange,
}: RegisteredSectionCardProps) => (
  <BaseCard isDisabled testID={baseTestID}>
    <VStack gap={16}>
      <TextTag text={GroupRegistrationText.Registered} variant="success" />
      <VStack gap={8}>
        {/* schedule event view leads with the tapped occurrence's date */}
        {eventDateString && (
          <GroupSectionDetailRow
            icon={FAIconName.CalendarDay}
            testID={joinTestIDs(baseTestID, "event-date")}
            text={eventDateString}
          />
        )}
        <GroupSectionDetailRow
          icon={FAIconName.Refresh}
          testID={joinTestIDs(baseTestID, "recurrence")}
          text={recurrenceString}
        />
        <GroupSectionDetailRow
          icon={FAIconName.Clock}
          testID={joinTestIDs(baseTestID, "time-range")}
          text={timeRange}
        />
        {/* group details view shows the section's next event date */}
        {nextDateString && (
          <GroupSectionDetailRow
            icon={FAIconName.Forward}
            testID={joinTestIDs(baseTestID, "next-date")}
            text={nextDateString}
          />
        )}
        {sessionCount > 0 && (
          <GroupSectionDetailRow
            icon={FAIconName.ArrowsLeftRight}
            testID={joinTestIDs(baseTestID, "sessions")}
            text={`${sessionCount} session${pluralize({ itemCount: sessionCount })}`}
          />
        )}
      </VStack>
      {(!isNil(onEditPress) || !isNil(onDeclineEventPress)) && (
        <HStack alignItems="center" gap={12}>
          {!isNil(onEditPress) && (
            <Button
              leftIcon={FAIconName.Pen}
              onPress={onEditPress}
              size="small"
              testID={joinTestIDs(baseTestID, "edit")}
              title={GroupRegistrationText.EditRegistration}
              variant="secondary"
            />
          )}
          {!isNil(onDeclineEventPress) && (
            <Button
              onPress={onDeclineEventPress}
              size="small"
              testID={joinTestIDs(baseTestID, "decline-event")}
              title={GroupRegistrationText.DeclineEvent}
              variant="tertiary"
            />
          )}
        </HStack>
      )}
    </VStack>
  </BaseCard>
);

export default RegisteredSectionCard;
